import { TrashIcon } from '@src/components/icons/TrashIcon';
import { useTranslation } from 'react-i18next';
import { Button, Hint, Stack, showDialog } from '@src/components';

export function HelpDeskDeleteAccount({
  id,
  name,
  onDeleted,
  onDenied,
}: {
  id: number;
  name: string;
  onDeleted: () => void;
  onDenied: () => void;
}) {
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const label = ru ? 'Удалить аккаунт' : 'Delete account';
  function confirm(error?: string) {
    showDialog({
      title: `${label} · #${id}`,
      children: (
        <Stack gap={3}>
          <p>
            {ru
              ? `Удалить данные аккаунта «${name}» навсегда?`
              : `Permanently delete account data for “${name}”?`}
          </p>
          <p>
            {ru
              ? 'Имя, email, данные карточек, ответы на вопросы и сообщения пользователя будут очищены. Вход и личный Премиум будут отключены. Восстановить данные нельзя.'
              : 'The user’s name, email, card details, questionnaire answers and messages will be cleared. Login and personal Premium will be disabled. This cannot be undone.'}
          </p>
          <p>
            {ru
              ? 'Сначала архивируйте игры пользователя. Активная жеребьёвка у организатора блокирует удаление. Неархивные игры организатора без такой защиты удаляются, архивные игры сохраняются с обезличенными карточками. История платежей сохраняется; возврат денег не выполняется.'
              : 'Archive the user’s games first. An organizer’s active draw blocks deletion. Unarchived organized games without that protection are deleted; archived games remain with anonymized cards. Payment history is retained; no refund is issued.'}
          </p>
          {error && <Hint testId="help-desk-delete-account-error">{error}</Hint>}
        </Stack>
      ),
      cancel: ru ? 'Отмена' : 'Cancel',
      closeLabel: ru ? 'Закрыть' : 'Close',
      submit: ru ? 'Удалить навсегда' : 'Delete permanently',
      submitVariant: 'danger',
      testId: 'help-desk-delete-account-confirm',
      onSubmit: async () => {
        try {
          const response = await fetch(`/api/admin/help-desk/users/${id}`, {
            method: 'DELETE',
            credentials: 'same-origin',
          });
          if ([401, 403].includes(response.status)) {
            onDenied();
            return;
          }
          if (!response.ok) {
            const text = await response.text();
            throw new Error(
              text.includes('CANNOT_DELETE_ACCOUNT_USER_HAS_ACTIVE_BOXES')
                ? ru
                  ? 'Сначала архивируйте игры с активной жеребьёвкой, затем повторите удаление.'
                  : 'Archive games with an active draw before deleting this account.'
                : ru
                ? 'Не удалось удалить аккаунт. Проверьте игры и повторите попытку.'
                : 'Could not delete account. Check their games and retry.',
            );
          }
          onDeleted();
        } catch (error) {
          confirm(error instanceof Error ? error.message : String(error));
          throw error;
        }
      },
    });
  }
  return (
    <Button
      variant="ghost"
      className="help-desk__action-button"
      icon={<TrashIcon />}
      aria-label={label}
      title={label}
      onClick={() => confirm()}
    />
  );
}
